import { useEffect } from 'react'
import { useQuery, useSubscription } from '@apollo/client'
import apolloClient from './apolloClient'
import debounce from 'lodash/debounce'
import { error, success } from '@react/react-spectrum/Toast'

import A from 'services/analytics'
import { dsnProjectApi, useDsnApiV2 } from 'services/axios'

import { sha256 } from 'utils/crypto'
import { readApolloError } from 'utils/errors'

import { PATHS, PROJECT_TYPES } from 'config/constants'
import {
  PROJECT_PART_UPDATED_SUBSCRIPTION,
  PROJECT_SCREEN_UPDATED_SUBSCRIPTION,
  CREATE_PROJECT_MUTATION,
  NEW_PROJECT_FRAGMENT,
  PROJECT_QUERY,
  UPDATE_PROJECT_PART_MUTATION,
  UPDATE_PROJECT_SCREEN_MUTATION,
  UPDATE_PROJECT_MUTATION,
  DELETE_LAUNCH_MUTATION,
  DELETE_DATASTREAM_MUTATION,
  ARCHIVE_PROJECT_MUTATION,
  MY_PROJECTS_QUERY,
  DUPLICATE_PROJECT_MUTATION,
  TRANSFER_PROJECT_OWNERSHIP,
  UPDATE_PERMISSION,
  UPDATE_PASSWORD,
  UPDATE_METADATA,
  DELETE_METADATA,
  UPDATE_PUBLIC_STATUS,
  GET_PROJECT_TEMPLATES,
  RENAME_PROJECT_SCREEN_MUTATION,
} from 'config/operations'

export function useProjectSubscription(projectId, partName = '') {
  const { data } = useSubscription(PROJECT_PART_UPDATED_SUBSCRIPTION, {
    variables: { projectId, partName },
  })
  return data?.projectPartUpdated
}

export function useScreenSubscription(projectId) {
  const { data } = useSubscription(PROJECT_SCREEN_UPDATED_SUBSCRIPTION, {
    variables: { projectId },
  })
  return data?.projectScreenUpdated
}

export async function createProject(templateId, title, thumbnail, environment) {
  try {
    if (!templateId) {
      throw new Error('Template is required to create a new project')
    }

    const response = await apolloClient.mutate({
      mutation: CREATE_PROJECT_MUTATION,
      variables: { templateId, title, thumbnail, environment },
      errorPolicy: 'none',
      update(cache, { data: { createProject } }) {
        cache.modify({
          fields: {
            myProjects(existingProjects = []) {
              const newProjectRef = cache.writeFragment({
                data: createProject,
                fragment: NEW_PROJECT_FRAGMENT,
              })
              return [...existingProjects, newProjectRef]
            },
          },
        })
      },
    })

    return response.data.createProject
  } catch (errors) {
    error('Could not create project. ' + readApolloError(errors).message)
    console.error('Could not create project. ' + readApolloError(errors).message)
    return null
  }
}

export function useProjectQuery(id) {
  const { subscribeToMore, ...response } = useQuery(PROJECT_QUERY, {
    variables: { id },
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    subscribeToMore({
      document: PROJECT_PART_UPDATED_SUBSCRIPTION,
      variables: { projectId: id },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data?.projectPartUpdated) return prev

        const { partName, partContent, projectId } = subscriptionData.data.projectPartUpdated

        if (projectId !== id) return prev
        if (partName === 'screens') return prev
        return { project: { ...prev.project, [partName]: partContent } }
      },
    })

    subscribeToMore({
      document: PROJECT_SCREEN_UPDATED_SUBSCRIPTION,
      variables: { projectId: id },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data?.projectScreenUpdated) return prev

        const { projectId, screenName, payload } = subscriptionData.data.projectScreenUpdated

        if (projectId !== id || !screenName || !payload) return prev
        return {
          project: {
            ...prev.project,
            screens: {
              ...prev.project.screens,
              [screenName]: { ...prev.project.screens[screenName], content: payload.content, meta: payload.meta },
            },
          },
        }
      },
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return response
}

const debouncedUpdateProjectPart = debounce((id, partName, partContent) => {
  A.triggerUpdateProjectPart(partName)

  return apolloClient.mutate({
    mutation: UPDATE_PROJECT_PART_MUTATION,
    variables: { id, partName, partContent },
  })
}, 1000)

export const updateProjectPart = async (id, partName, partContent, immediate = false) => {
  // Update cache to display changes in UI immediately
  const data = apolloClient.readQuery({ query: PROJECT_QUERY, variables: { id } })
  apolloClient.writeQuery({
    query: PROJECT_QUERY,
    variables: { id },
    data: { ...data, project: { ...data.project, [partName]: partContent } },
  })

  if (immediate) {
    // Immediately send update request and set expected outcome in cache to refresh UI
    A.triggerUpdateProjectPart(partName)

    return await apolloClient.mutate({
      mutation: UPDATE_PROJECT_PART_MUTATION,
      variables: { id, partName, partContent },
    })
  } else {
    // Start timer for debounced network call
    debouncedUpdateProjectPart(id, partName, partContent)
  }
}

export const updateProjectScreen = async (id, screenName, payload) => {
  // Update cache to display changes in UI immediately
  const data = apolloClient.readQuery({ query: PROJECT_QUERY, variables: { id } })

  const newScreens = { ...data.project.screens, [screenName]: payload }

  if (!payload) {
    delete newScreens[screenName]
  }

  apolloClient.writeQuery({
    query: PROJECT_QUERY,
    variables: { id },
    data: { ...data, project: { ...data.project, screens: newScreens } },
  })

  A.triggerUpdateProjectPart('screens')

  return await apolloClient.mutate({
    mutation: UPDATE_PROJECT_SCREEN_MUTATION,
    variables: { id, screenName, payload },
  })
}

export const renameProjectScreen = async (id, oldScreenName, newScreenName, newScreenTitle) => {
  // Update cache to display changes in UI immediately
  const data = apolloClient.readQuery({ query: PROJECT_QUERY, variables: { id } })

  const newScreens = {
    ...data.project.screens,
    [newScreenName]: {
      ...data.project.screens[oldScreenName],
      meta: {
        ...data.project.screens[oldScreenName].meta,
        name: newScreenName,
        title: newScreenTitle || data.project.screens[oldScreenName].meta.title,
      },
    },
  }

  apolloClient.writeQuery({
    query: PROJECT_QUERY,
    variables: { id },
    data: { ...data, project: { ...data.project, screens: newScreens } },
  })

  A.triggerUpdateProjectPart('screens')

  const res = await apolloClient.mutate({
    mutation: RENAME_PROJECT_SCREEN_MUTATION,
    variables: { id, oldScreenName, newScreenName, newScreenTitle },
  })
  const { meta, screens } = res?.data?.renameProjectScreen || {}

  if (meta || screens) {
    const updatedMeta = meta || data.project.meta
    apolloClient.writeQuery({
      query: PROJECT_QUERY,
      variables: { id },
      data: {
        ...data,
        project: {
          ...data.project,
          meta: updatedMeta,
          screens: {
            ...data.project.screens,
            ...screens,
          },
        },
      },
    })
  }

  return res
}

export const saveProject = async (projectData) => {
  let project = { ...projectData }
  const id = project.meta && project?.meta?.id

  await apolloClient.mutate({
    mutation: UPDATE_PROJECT_MUTATION,
    variables: { id, content: projectData },
  })

  return project
}
export const deleteLaunchProperty = async (id) => {
  if (!id) {
    throw new Error('Project ID and Property ID are required for deletion.')
  }

  const response = await apolloClient.mutate({
    mutation: DELETE_LAUNCH_MUTATION,
    variables: { id },
  })

  return response?.data
}

export const deleteDataStream = async (propertyID) => {
  if (!propertyID) {
    throw new Error('Project ID and Property ID are required for deletion.')
  }

  const response = await apolloClient.mutate({
    mutation: DELETE_DATASTREAM_MUTATION,
    variables: { propertyID },
  })

  return response?.data
}
export const archiveProject = async (id) => {
  return await apolloClient.mutate({
    mutation: ARCHIVE_PROJECT_MUTATION,
    variables: { id },
    optimisticResponse: { archiveProject: true },
    update(cache) {
      const data = cache.readQuery({ query: MY_PROJECTS_QUERY })
      cache.writeQuery({
        query: MY_PROJECTS_QUERY,
        data: { ...data, myProjects: data.myProjects.filter((p) => p.id !== id) },
      })
    },
  })
}

export const duplicateProject = async (id, title) => {
  return apolloClient.mutate({
    mutation: DUPLICATE_PROJECT_MUTATION,
    variables: { id, title },
    update(cache, { data: { duplicateProject } }) {
      cache.modify({
        fields: {
          myProjects(existingProjects = []) {
            const newProjectRef = cache.writeFragment({
              data: duplicateProject,
              fragment: NEW_PROJECT_FRAGMENT,
            })
            return [...existingProjects, newProjectRef]
          },
        },
      })
    },
  })
}

// No need to update cache, we reload the project anyway
export const trasferProjectOwnership = async (projectId, newOwner) => {
  try {
    const response = await apolloClient.mutate({
      mutation: TRANSFER_PROJECT_OWNERSHIP,
      variables: { id: projectId, newOwner },
    })

    if (response.errors) {
      error('Could not transfer ownership. ' + readApolloError(response.errors).message)
    } else {
      success(`Project ownership transfered to ${response.data.transferProjectOwnership.owner}. Reloading project.`)
      const newProjectId = response?.data?.transferProjectOwnership?.id
      if (newProjectId) {
        setTimeout(() => (window.location.pathname = PATHS.PROJECT_DETAILS + newProjectId + '/access'))
      } else {
        setTimeout(() => (window.location.href = window.location.origin))
      }
    }
  } catch (e) {
    error('Could not transfer ownership. ' + readApolloError(e).message)
  }
}

// No need to update cache - this doesn't affect the user
export const updatePermission = async (projectId, targetUser, permission) => {
  const response = await apolloClient.mutate({
    mutation: UPDATE_PERMISSION,
    variables: { id: projectId, targetUser, permission },
  })

  return response.data?.updateProjectPermission
}

// No need to update cache - this doesn't affect the user
export const updatePassword = async (id, password) => {
  const hashedPassword = await (password ? sha256(password) : password)
  const response = await apolloClient.mutate({
    mutation: UPDATE_PASSWORD,
    variables: { id, password: hashedPassword },
  })

  return response.data?.updateProjectPassword
}

// No need to update cache - this doesn't affect the user
export const updateMetadata = async (id, metadata, project) => {
  try {
    const enrichedMetadata = {
      ...metadata,
      projectID: id,
    }

    const response = await apolloClient.mutate({
      mutation: UPDATE_METADATA,
      variables: { id, metadata: enrichedMetadata },
    })

    if (response.errors?.[0]) {
      throw new Error(response.errors[0].message)
    }

    success('Project metadata updated successfully')
    return response.data?.upsertProjectMetadata
  } catch (e) {
    console.error(e)
    error(e.message)
  }
}

export const deleteMetadata = async (id) => {
  try {
    const response = await apolloClient.mutate({
      mutation: DELETE_METADATA,
      variables: { id },
    })

    if (response.errors?.[0]) {
      throw new Error(response.errors[0].message)
    }

    success('Project metadata updated successfully')
    return response.data?.upsertProjectMetadata?.metadata
  } catch (e) {
    console.error(e)
    error(e.message)
  }
}

// No need to update cache - this doesn't affect the user
export const updatePublicStatus = async (projectId, status) => {
  const response = await apolloClient.mutate({
    mutation: UPDATE_PUBLIC_STATUS,
    variables: { id: projectId, status },
  })

  return response.data?.updateProjectPublicStatus
}

export const getProjectTemplates = async () => {
  try {
    const response = await apolloClient.query({
      query: GET_PROJECT_TEMPLATES,
      variables: {},
    })

    return response?.data?.projectTemplates || []
  } catch (errors) {
    error('Could not read template list. ' + readApolloError(errors).message)
    console.error('Could not read template list. ' + readApolloError(errors).message)
    return []
  }
}

export function useProjectInsights(projectId) {
  return useDsnApiV2({
    url: '/analytics/project-insights',
    method: 'GET',
    params: { projectId },
  })
}

export const getConfig = (type) => PROJECT_TYPES[type?.toUpperCase().replaceAll('-', '_')]

export async function getPreviewToken(projectId) {
  const response = await dsnProjectApi.get(`/${projectId}/token`, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return response.data.token
}
